import { DesignFormSchema } from '@app/domain/template/api/template.form.schema.api';
import { ECodeType, ELinkType } from '@app/swagger-types';
import { email, oneOfEnum } from '@app/utils/yup.utils';
import { object, InferType, string, number, array } from 'yup';
const getPhoneInputLib = () => import('react-phone-number-input');

export const regexUrlWithoutProtocol = new RegExp(
  '^((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
  'i'
);

export const regexUrlWithProtocol = new RegExp(
  '^([a-zA-Z]+:\\/\\/)' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
  'i'
);

export const VCardLinkFormSchema = object({
  linkType: oneOfEnum(ELinkType),
  url: string(),
});
export type VCardLinkFormSchema = InferType<typeof VCardLinkFormSchema>;

export const VCardFormSchema = object()
  .notRequired()
  .nullable()
  .when(['codeType'], {
    is: (codeType: ECodeType) => codeType === ECodeType.V_CARD,
    then: (schema) =>
      schema.shape({
        fullName: string().required('Required field'),
        phoneNumber: string()
          .required('Required field')
          .test('phone-number', 'Invalid phone number', async (phone) => {
            const { isValidPhoneNumber } = await getPhoneInputLib();
            return isValidPhoneNumber(phone || '');
          }),
        companyName: string().nullable(),
        jobPosition: string().nullable(),
        alternativePhoneNumber: string()
          .nullable()
          .test('phone-number', 'Invalid phone number', async (phone) => {
            if (!phone) {
              return true;
            }

            const { isValidPhoneNumber } = await getPhoneInputLib();
            return isValidPhoneNumber(phone || '');
          }),
        libraryId: string(),
        email: email().notRequired(),
        website: string()
          .nullable()
          .test('invalid-website-url', 'Invalid website url', (websiteUrl) => {
            return !websiteUrl || regexUrlWithProtocol.test(websiteUrl || '');
          }),
        street: string().nullable(),
        postalCode: string().nullable(),
        city: string().nullable(),
        state: string().nullable(),
        country: string().nullable(),
        designPrimaryColor: string().required('Required field'),
        designSecondaryColor: string().required('Required field'),
        designNameColor: string().required('Required field'),
        designTitleColor: string().required('Required field'),
        designTextColor: string().required('Required field'),
        designIconColor: string().required('Required field'),
        designTextHoverColor: string().required('Required field'),
        buttonText: string().required('Required field'),
        buttonSize: number().required('Required field'),
        buttonBgColor: string().required('Required field'),
        buttonTextColor: string().required('Required field'),
        buttonHoverColor: string().required('Required field'),
        buttonBorderColor: string().required('Required field'),
        links: array().of(VCardLinkFormSchema),
      }),
    // TODO: remove duplicate schema in otherwise
    otherwise: (schema) =>
      schema.shape({
        fullName: string(),
        libraryId: string(),
        phoneNumber: string(),
        alternativePhoneNumber: string().nullable(),
        email: string(),
        website: string(),
        companyName: string(),
        jobPosition: string(),
        street: string(),
        postalCode: string(),
        city: string(),
        state: string(),
        country: string(),
        designPrimaryColor: string(),
        designSecondaryColor: string(),
        designNameColor: string(),
        designTitleColor: string(),
        designTextColor: string(),
        designIconColor: string(),
        designTextHoverColor: string(),
        buttonText: string(),
        buttonSize: number(),
        buttonBgColor: string(),
        buttonTextColor: string(),
        buttonHoverColor: string(),
        buttonBorderColor: string(),
        links: array().of(VCardLinkFormSchema),
      }),
  });
export type VCardFormSchema = InferType<typeof VCardFormSchema>;

export const QRCodeFormSchema = object({
  codeType: oneOfEnum(ECodeType),
  websiteUrl: string()
    .test(
      'required-website-url',
      'Required field',
      (websiteUrl, { parent: { codeType } }: { parent: { codeType: ECodeType } }) => {
        if (codeType === ECodeType.WEBSITE) {
          return !!websiteUrl;
        }
        return true;
      }
    )
    .test(
      'invalid-website-url',
      'Invalid website url',
      (websiteUrl, { parent: { codeType } }: { parent: { codeType: ECodeType } }) => {
        if (codeType === ECodeType.WEBSITE) {
          return regexUrlWithProtocol.test(websiteUrl || '');
        }
        return true;
      }
    ),
  templateId: string().nullable(),
  name: string().required('Required field'),
  vcard: VCardFormSchema,
}).concat(DesignFormSchema.omit(['template']));
export type QRCodeFormSchema = InferType<typeof QRCodeFormSchema>;

export const QRCodeNameFormSchema = QRCodeFormSchema.pick(['name']);
export type QRCodeNameFormSchema = InferType<typeof QRCodeNameFormSchema>;
